/**
 * How a file built in the browser reaches the reader's disk — the one seam, so a test can measure
 * the FILE rather than the browser.
 *
 * CAOS-1837 — this lived in `roster-page.ts` as CAOS-551's CSV saver and moved here unchanged in
 * behaviour, because a second surface now needs it: a `rosterAction` whose response carries a file
 * (`data export`) saves it through this same function, and `roster-page.ts` already imports from
 * `roster-actions.ts`, so a seam living in the page module could not be reached from the action
 * module without a cycle. A duplicate would have been two things to keep in step — and the one
 * that drifts is always the one with fewer readers.
 *
 * The CONTENT TYPE is a parameter rather than a constant for the same reason: a roster download is
 * always CSV, and an authored action's response says what it is. It defaults to CSV so the roster
 * call sites read exactly as they did.
 */

/** A file, named and typed, on its way to the reader's disk. */
export type SaveFile = (filename: string, content: string, contentType?: string) => void;

/** The CSV media type — the default, and the one that gets a byte-order mark. */
export const CSV_CONTENT_TYPE = 'text/csv;charset=utf-8';

/**
 * The file's text, with the UTF-8 announcement a spreadsheet needs and nothing else gets.
 *
 * Excel reads a CSV in the system's legacy encoding unless the file announces UTF-8, so a name with
 * an accent in it arrives mangled. The byte-order mark is that announcement, and it belongs to the
 * FILE rather than to whoever built the text.
 *
 * CSV ONLY. A BOM at the head of a JSONL file is not a declaration, it is a stray character on the
 * first line, and a strict JSON parser rejects that line — so the announcement that rescues one
 * format breaks the other. Keyed off the media type rather than the extension, because the type is
 * what the writer actually said.
 *
 * Separated from {@link defaultSaveFile} so this decision is provable without a browser: the rest
 * of that function is `Blob`, `URL.createObjectURL` and a synthetic click, which is exactly why
 * every caller injects a seam rather than exercising it.
 */
export function withEncodingMark(content: string, contentType: string): string {
  return contentType.startsWith('text/csv') ? `\ufeff${content}` : content;
}

export const defaultSaveFile: SaveFile = (filename, content, contentType = CSV_CONTENT_TYPE) => {
  const blob = new Blob([withEncodingMark(content, contentType)], { type: contentType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.click();
  // Released on the next turn: revoking it in the same one can beat the browser's own read of it.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};
